import { useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect } from "react";
import { X, ExternalLink } from "lucide-react";

// ─── formatters ────────────────────────────────────────────────────────────
const short = (a) => (a ? `${a.slice(0, 4)}…${a.slice(-4)}` : "");
const fmtAmount = (n) => {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return Math.round(n).toString();
};
const fmtDate = (unixSec) => {
  if (!unixSec) return "unknown";
  try {
    return new Date(unixSec * 1000).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return "unknown"; }
};
const hashCode = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

// ─── layout: pack circles by amount, biggest in center ────────────────────
// Simple front-based greedy packer:
//   1. Place biggest at (0,0).
//   2. For each next bubble, try every existing bubble as an "anchor"
//      and place tangent to it (angle sampled around) — pick the position
//      whose distance to origin is smallest (grows outward from center).
// Reserve a central "hub" slot so bubbles pack around the center image instead of on top of it.
const packBubbles = (transfers, W, H) => {
  if (!transfers?.length) return { bubbles: [], centerR: 0 };
  const sorted = [...transfers].sort((a, b) => (b.amount || 0) - (a.amount || 0));
  const maxAmount = sorted[0]?.amount || 1;

  // radius range scales with container so it always fits
  const shortSide = Math.min(W, H);
  const minR = Math.max(4, shortSide * 0.010);
  const maxR = Math.max(minR + 6, shortSide * 0.068);

  const radiusFor = (amount) => {
    const t = Math.sqrt(Math.max(amount, 0) / maxAmount); // sqrt scale for area feel
    return minR + (maxR - minR) * t;
  };

  // Reserved center circle for the hub image. Slightly larger than the biggest bubble.
  const centerR = maxR * 1.15;
  const placed = [{ x: 0, y: 0, r: centerR, __reserved: true }];
  const PAD = 1.0;

  sorted.forEach((t, i) => {
    const r = radiusFor(t.amount || 0);
    // Try to place tangent to some existing bubble.
    let best = null;
    // Sample a subset of anchors to keep it O(N*samples) not O(N^2*samples).
    const anchorPool = placed.length > 40 ? placed.slice(-40).concat(placed.slice(0, 20)) : placed;
    const SAMPLES = 36;
    // hash-based angle offset so packing looks organic, not perfectly stratified
    const angOffset = (hashCode(t.recipient + t.signature) % 360) * (Math.PI / 180);
    for (const anchor of anchorPool) {
      const d = anchor.r + r + PAD;
      for (let s = 0; s < SAMPLES; s++) {
        const ang = angOffset + (s / SAMPLES) * Math.PI * 2;
        const cx = anchor.x + Math.cos(ang) * d;
        const cy = anchor.y + Math.sin(ang) * d;
        // check for overlap with all placed
        let ok = true;
        for (const p of placed) {
          const dx = cx - p.x;
          const dy = cy - p.y;
          const minD = p.r + r + PAD - 0.001;
          if (dx * dx + dy * dy < minD * minD) { ok = false; break; }
        }
        if (!ok) continue;
        // prefer closest to origin (grow from center outward)
        const dist2 = cx * cx + cy * cy;
        if (!best || dist2 < best.dist2) best = { x: cx, y: cy, dist2 };
      }
    }
    if (best) {
      placed.push({ ...t, x: best.x, y: best.y, r, rank: i + 1 });
    } else {
      // extremely unlikely fallback — put somewhere randomish
      const ang = Math.random() * Math.PI * 2;
      const rd = shortSide * 0.4;
      placed.push({
        ...t, x: Math.cos(ang) * rd, y: Math.sin(ang) * rd, r, rank: i + 1,
      });
    }
  });

  // strip out the reserved center; return real bubbles + centerR for the hub
  const bubbles = placed.filter((p) => !p.__reserved);
  return { bubbles, centerR };
};

// ─── color palette (rank-based) ────────────────────────────────────────────
// All within the site's purple/cyan/green family. Top tier = brightest hot violet.
const colorForRank = (rank, total) => {
  const pct = rank / Math.max(total, 1);
  if (pct <= 0.04) return { core: "#E879FF", ring: "#F5B7FF", glow: "rgba(232,121,255,0.45)", text: "#170822" };
  if (pct <= 0.20) return { core: "#14F195", ring: "#7BFFD1", glow: "rgba(20,241,149,0.40)", text: "#03130a" };
  if (pct <= 0.55) return { core: "#00C2FF", ring: "#7ee2ff", glow: "rgba(0,194,255,0.28)", text: "#02121b" };
  return { core: "#7A3BD8", ring: "#B98EFF", glow: "rgba(122,59,216,0.22)", text: "#f5e8ff" };
};

// ─── main component ───────────────────────────────────────────────────────
export const BubbleMap = ({ transfers, newDropTick = 0 }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const bubblesRef = useRef([]);
  const centerRRef = useRef(0);
  const hubImgRef = useRef(null);
  const sizeRef = useRef({ w: 0, h: 0 });
  const hoverIdxRef = useRef(-1);
  const [selected, setSelected] = useState(null); // { transfer, screenX, screenY }
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [newBadge, setNewBadge] = useState(null); // { key, x, y }

  const community = useMemo(
    () => (transfers || []).filter((t) => !t.isExcluded),
    [transfers]
  );
  const uniqueWallets = useMemo(
    () => new Set(community.map((t) => t.recipient)).size,
    [community]
  );

  // Flash the "NEW" badge only when the parent signals a real backend increment.
  // (newDropTick starts at 0 and only bumps AFTER a genuine new tx is detected.)
  useEffect(() => {
    if (newDropTick <= 0) return undefined;
    // Anchor to the largest bubble (index 0 = biggest amount after packing).
    const largest = bubblesRef.current[0];
    if (!largest || !sizeRef.current.w) return undefined;
    const cx = sizeRef.current.w / 2;
    const cy = sizeRef.current.h / 2;
    setNewBadge({
      key: newDropTick,
      x: cx + largest.x,
      y: cy + largest.y - largest.r - 6, // just above the bubble
    });
    const t = setTimeout(() => setNewBadge(null), 3200);
    return () => clearTimeout(t);
  }, [newDropTick]);

  // Preload the hub image once, preferring AVIF → WebP → JPEG fallback.
  useEffect(() => {
    const candidates = [
      "/assets/curb-core.avif",
      "/assets/curb-core.webp",
      "/assets/curb-core.jpeg",
    ];
    let cancelled = false;
    const tryNext = (i) => {
      if (cancelled || i >= candidates.length) return;
      const img = new Image();
      img.onload = () => { if (!cancelled) hubImgRef.current = img; };
      img.onerror = () => tryNext(i + 1);
      img.src = candidates[i];
    };
    tryNext(0);
    return () => { cancelled = true; };
  }, []);

  // ─── measure container ─────────────────────────────────────────────────
  useLayoutEffect(() => {
    if (!wrapRef.current) return undefined;
    const el = wrapRef.current;
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      const w = Math.max(240, rect.width);
      // maintain a nice landscape-ish canvas on desktop, closer to square on mobile
      const h = rect.width < 640 ? Math.round(w * 0.95) : Math.round(w * 0.62);
      sizeRef.current = { w, h };
      setSize({ w, h });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ─── (re)pack whenever data or size changes ────────────────────────────
  useEffect(() => {
    if (!size.w || !size.h) return;
    const { bubbles: packed, centerR } = packBubbles(community, size.w, size.h);
    // Assign an ambient drift + pulse phase per bubble
    bubblesRef.current = packed.map((b, i) => ({
      ...b,
      phase: (hashCode(b.recipient + b.signature) % 1000) / 1000, // 0..1
      driftAmp: 0.6 + (i % 5) * 0.15, // small px amplitude
    }));
    centerRRef.current = centerR;
    hoverIdxRef.current = -1;
  }, [community, size.w, size.h]);

  // ─── render loop ──────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !size.w || !size.h) return undefined;
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = Math.round(size.w * dpr);
    canvas.height = Math.round(size.h * dpr);
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Mobile budget: keep the map visually alive but let the CPU breathe.
    // - Only the top MOBILE_ANIM_CAP bubbles get sin() pulse/drift each frame.
    // - Pause rAF entirely while the user is actively scrolling.
    // - Halve the animation amplitude so the compositor barely needs to redraw.
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const MOBILE_ANIM_CAP = 50;
    const ANIM_AMP_MUL = isMobile ? 0.5 : 1;

    const cx = size.w / 2;
    const cy = size.h / 2;
    const start = performance.now();

    // Track scroll state on mobile only — while scrolling, redraw a single
    // "frozen" frame and skip further updates until scroll ends.
    let scrollingActive = false;
    let scrollTimer = null;
    const onScroll = () => {
      scrollingActive = true;
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => { scrollingActive = false; }, 160);
    };
    if (isMobile) window.addEventListener("scroll", onScroll, { passive: true });

    const draw = (t) => {
      // On mobile, skip the whole render pass while scrolling — the last
      // painted frame stays on screen (native scroll is butter-smooth then).
      if (isMobile && scrollingActive) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const dt = (t - start) / 1000;
      ctx.clearRect(0, 0, size.w, size.h);

      // subtle radial vignette / halo — gives depth
      const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(size.w, size.h) * 0.6);
      bg.addColorStop(0, "rgba(153,69,255,0.10)");
      bg.addColorStop(0.55, "rgba(20,241,149,0.03)");
      bg.addColorStop(1, "rgba(5,4,9,0)");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, size.w, size.h);

      // draw bubbles
      const bubbles = bubblesRef.current;
      const total = bubbles.length;
      const hoveredIdx = hoverIdxRef.current;

      // Which bubbles get expensive sin/cos per frame? On desktop: all.
      // On mobile: only the top MOBILE_ANIM_CAP (biggest / most visible).
      const animCap = isMobile ? Math.min(MOBILE_ANIM_CAP, total) : total;

      // Cheap helper: returns the animated (bx, by, rr, pulse) for a bubble.
      // For indices above `animCap` we use the static base values — no trig.
      const posFor = (b, i) => {
        if (i >= animCap) {
          return { bx: cx + b.x, by: cy + b.y, rr: b.r, pulse: 1 };
        }
        const pulse = 1 + Math.sin(dt * 0.7 + b.phase * Math.PI * 2) * 0.03;
        const amp = b.driftAmp * ANIM_AMP_MUL;
        const driftX = Math.sin(dt * 0.6 + b.phase * 6.283) * amp;
        const driftY = Math.cos(dt * 0.55 + b.phase * 6.283) * amp;
        return { bx: cx + b.x + driftX, by: cy + b.y + driftY, rr: b.r * pulse, pulse };
      };

      // PASS 1: soft glows only for large / top-tier bubbles (keeps the scene clean)
      for (let i = 0; i < bubbles.length; i++) {
        const b = bubbles[i];
        const pct = (i + 1) / Math.max(total, 1);
        // Only glow the top ~20% or bubbles bigger than a threshold
        if (pct > 0.20 && b.r < 22) continue;
        const { bx, by, rr } = posFor(b, i);
        const c = colorForRank(b.rank, total);
        const g = ctx.createRadialGradient(bx, by, rr * 0.4, bx, by, rr * 1.9);
        g.addColorStop(0, c.glow);
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(bx, by, rr * 1.9, 0, Math.PI * 2);
        ctx.fill();
      }

      // PASS 2: bubble bodies + rims + labels
      for (let i = 0; i < bubbles.length; i++) {
        const b = bubbles[i];
        const { bx, by, rr, pulse } = posFor(b, i);
        const c = colorForRank(b.rank, total);

        // solid body — flat fill keeps hue distinct and prevents wash-out
        ctx.fillStyle = c.core;
        ctx.beginPath();
        ctx.arc(bx, by, rr, 0, Math.PI * 2);
        ctx.fill();

        // subtle top-left specular highlight
        const spec = ctx.createRadialGradient(
          bx - rr * 0.4, by - rr * 0.45, rr * 0.05,
          bx - rr * 0.4, by - rr * 0.45, rr * 0.9
        );
        spec.addColorStop(0, "rgba(255,255,255,0.32)");
        spec.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = spec;
        ctx.beginPath();
        ctx.arc(bx, by, rr, 0, Math.PI * 2);
        ctx.fill();

        // rim
        ctx.strokeStyle = i === hoveredIdx ? "#ffffff" : c.ring;
        ctx.lineWidth = i === hoveredIdx ? 2 : 1;
        ctx.beginPath();
        ctx.arc(bx, by, rr, 0, Math.PI * 2);
        ctx.stroke();

        // label — visibility uses BASE radius (b.r) so it never blinks on/off with the pulse.
        // Font size is a smooth float (no integer snapping); scale gently with the same
        // pulse factor for a calm breathing effect.
        if (b.r >= 15) {
          ctx.fillStyle = c.text;
          const baseFont = Math.min(b.r * 0.44, 20);
          const fontPx = baseFont * pulse; // smooth sine-driven scale
          ctx.font = `700 ${fontPx.toFixed(2)}px 'Bebas Neue', sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(fmtAmount(b.amount), bx, by);
        }
      }

      // ─── PASS 3: center hub (image + glow ring) ────────────────────────
      const centerR = centerRRef.current;
      if (centerR > 0) {
        // Slow pulsing glow ring behind the image
        const glowPulse = 1 + Math.sin(dt * 1.2) * 0.12;
        const gradR = centerR * 1.8;
        const hg = ctx.createRadialGradient(cx, cy, centerR * 0.6, cx, cy, gradR);
        hg.addColorStop(0, "rgba(232,121,255,0.35)");
        hg.addColorStop(0.5, "rgba(20,241,149,0.20)");
        hg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = hg;
        ctx.beginPath();
        ctx.arc(cx, cy, gradR * glowPulse, 0, Math.PI * 2);
        ctx.fill();

        // Backdrop disc so image never sees through
        ctx.fillStyle = "#050409";
        ctx.beginPath();
        ctx.arc(cx, cy, centerR, 0, Math.PI * 2);
        ctx.fill();

        // Clipped image
        const img = hubImgRef.current;
        if (img && img.complete) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(cx, cy, centerR - 2, 0, Math.PI * 2);
          ctx.clip();
          const d = (centerR - 2) * 2;
          ctx.drawImage(img, cx - centerR + 2, cy - centerR + 2, d, d);
          ctx.restore();
        }

        // Thin glowing rim (two-tone: violet + green blend)
        ctx.strokeStyle = "#E879FF";
        ctx.lineWidth = 2;
        ctx.shadowColor = "rgba(232,121,255,0.85)";
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(cx, cy, centerR - 1, 0, Math.PI * 2);
        ctx.stroke();
        ctx.shadowBlur = 0;
        // Inner green accent ring
        ctx.strokeStyle = "rgba(20,241,149,0.65)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(cx, cy, centerR - 4, 0, Math.PI * 2);
        ctx.stroke();
      }

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (isMobile) window.removeEventListener("scroll", onScroll);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [size.w, size.h]);

  // ─── hit testing ──────────────────────────────────────────────────────
  const bubbleAtPoint = useCallback((px, py) => {
    const cx = sizeRef.current.w / 2;
    const cy = sizeRef.current.h / 2;
    const bubbles = bubblesRef.current;
    // iterate in reverse — smaller bubbles are drawn later at edges,
    // but we want the closest hit; check all and keep closest.
    let best = { idx: -1, d2: Infinity };
    for (let i = 0; i < bubbles.length; i++) {
      const b = bubbles[i];
      const bx = cx + b.x;
      const by = cy + b.y;
      const dx = px - bx;
      const dy = py - by;
      const d2 = dx * dx + dy * dy;
      const rr = b.r + 2; // small forgiving tolerance for tap
      if (d2 <= rr * rr && d2 < best.d2) best = { idx: i, d2 };
    }
    return best.idx;
  }, []);

  const onCanvasMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const idx = bubbleAtPoint(px, py);
    hoverIdxRef.current = idx;
    canvas.style.cursor = idx >= 0 ? "pointer" : "default";
  };

  const onCanvasLeave = () => {
    hoverIdxRef.current = -1;
    if (canvasRef.current) canvasRef.current.style.cursor = "default";
  };

  const openBubble = (idx, clientX, clientY) => {
    if (idx < 0) return;
    const b = bubblesRef.current[idx];
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cx = sizeRef.current.w / 2;
    const cy = sizeRef.current.h / 2;
    // Anchor screen-relative to the bubble center (not the tap point)
    // so the popup follows a specific target consistently.
    const anchorX = rect.left + cx + b.x;
    const anchorY = rect.top + cy + b.y;
    // fallback to tap point if we don't have anchor for some reason
    setSelected({
      transfer: b,
      total: bubblesRef.current.length,
      anchorX: Number.isFinite(anchorX) ? anchorX : clientX,
      anchorY: Number.isFinite(anchorY) ? anchorY : clientY,
      bubbleR: b.r,
    });
  };

  const onCanvasClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const idx = bubbleAtPoint(px, py);
    openBubble(idx, e.clientX, e.clientY);
  };

  // Escape closes
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      ref={wrapRef}
      data-testid="airdrop-bubble-map"
      className="relative w-full"
    >
      <canvas
        ref={canvasRef}
        data-testid="airdrop-bubble-canvas"
        onMouseMove={onCanvasMove}
        onMouseLeave={onCanvasLeave}
        onClick={onCanvasClick}
        style={{
          display: "block",
          width: "100%",
          touchAction: "manipulation",
          borderRadius: 16,
        }}
      />

      {newBadge && (
        <span
          key={newBadge.key}
          data-testid="airdrop-new-badge"
          className="new-drop-badge pointer-events-none absolute font-bebas tracking-[0.2em] text-[10px] sm:text-xs px-2 py-0.5 rounded-full text-[#050409]"
          style={{
            left: newBadge.x,
            top: newBadge.y,
            transform: "translate(-50%, -100%)",
            background: "linear-gradient(90deg, #14F195 0%, #E879FF 100%)",
            boxShadow: "0 0 18px rgba(232,121,255,0.85), 0 0 6px rgba(20,241,149,0.7)",
            border: "1px solid rgba(255,255,255,0.6)",
          }}
        >
          NEW
        </span>
      )}

      <p
        className="mt-5 font-marker text-zinc-500 text-sm text-center px-4"
        data-testid="airdrop-bubble-caption"
      >
        {community.length.toLocaleString()} drops · {uniqueWallets.toLocaleString()} wallets fed · biggest bags at the core
      </p>

      {selected && (
        <BubblePopup
          data={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

// ─── tap popup — bounds-checked hover card ────────────────────────────────
const BubblePopup = ({ data, onClose }) => {
  const { transfer, total, anchorX, anchorY, bubbleR } = data;
  const popupRef = useRef(null);
  const [pos, setPos] = useState({ left: -9999, top: -9999, ready: false });

  const solscan = `https://solscan.io/tx/${transfer.signature}`;
  const walletUrl = `https://solscan.io/account/${transfer.recipient}`;

  useLayoutEffect(() => {
    const el = popupRef.current;
    if (!el) return;
    const cardRect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 10;
    // Preferred: center horizontally over the bubble, above it.
    let left = anchorX - cardRect.width / 2;
    let top = anchorY - bubbleR - gap - cardRect.height;
    // If not enough room above, put below.
    if (top < 8) top = anchorY + bubbleR + gap;
    // Horizontal clamp
    const pad = 8;
    if (left < pad) left = pad;
    if (left + cardRect.width > vw - pad) left = vw - pad - cardRect.width;
    // Vertical clamp — if still off-screen, dock at bottom edge
    if (top + cardRect.height > vh - pad) top = vh - pad - cardRect.height;
    if (top < pad) top = pad;
    setPos({ left, top, ready: true });
  }, [anchorX, anchorY, bubbleR]);

  // Close on outside click / touch
  useEffect(() => {
    const onDown = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };
    // use timeout so the current click that opened the popup doesn't immediately close it
    const id = setTimeout(() => {
      document.addEventListener("mousedown", onDown);
      document.addEventListener("touchstart", onDown, { passive: true });
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [onClose]);

  return (
    <div
      ref={popupRef}
      data-testid="airdrop-bubble-popup"
      className="fixed z-[80] rounded-2xl bg-[#0a0714]/95 border-2 border-[#14F195]/70 shadow-[0_10px_40px_rgba(20,241,149,0.35)] backdrop-blur-sm px-4 py-3"
      style={{
        left: pos.left,
        top: pos.top,
        width: "min(320px, 85vw)",
        opacity: pos.ready ? 1 : 0,
        transition: "opacity 120ms ease-out",
        pointerEvents: pos.ready ? "auto" : "none",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-bebas tracking-[0.28em] text-[10px] text-[#9945FF]">
          DROP #{transfer.rank || "?"} / {(total || 0).toLocaleString()}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-zinc-400 hover:text-[#14F195] -m-1 p-1 transition-colors"
          data-testid="airdrop-bubble-popup-close"
        >
          <X size={16} />
        </button>
      </div>

      <p className="font-marker text-3xl gradient-sol text-glow-green mt-0.5 leading-none break-words -rotate-1 inline-block">
        {fmtAmount(transfer.amount)} $CURB
      </p>
      <p className="text-zinc-500 text-[10px] mt-0.5">
        {transfer.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} raw
      </p>

      <div className="mt-3 space-y-1.5 text-xs">
        <PopupRow label="Recipient">
          <a
            href={walletUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-zinc-100 hover:text-[#14F195] font-mono inline-flex items-center gap-1"
          >
            {short(transfer.recipient)} <ExternalLink size={11} />
          </a>
        </PopupRow>
        <PopupRow label="When">
          <span className="text-zinc-200">{fmtDate(transfer.blockTime)}</span>
        </PopupRow>
        <PopupRow label="Tx">
          <a
            href={solscan}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#14F195] hover:text-white font-mono inline-flex items-center gap-1"
          >
            Solscan <ExternalLink size={11} />
          </a>
        </PopupRow>
      </div>
    </div>
  );
};

const PopupRow = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 border-b border-white/5 pb-1">
    <span className="text-zinc-500 text-[10px] font-bebas tracking-[0.25em]">{label}</span>
    {children}
  </div>
);

export default BubbleMap;
